<script setup lang="ts">
import { ref } from 'vue';
import { Field, useForm, useField } from 'vee-validate';
import { QuestionPhone, ToastTypes, QuestionEmail, ContactPhone, QuestionYesNo } from '@hfc/ui-core';
import { ADDRESS_TYPE } from '@hfc/common/src/types/AddressTypes';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import QuestionAddress from '@/application/shared/infrastructure/components/questions/QuestionAddress.vue';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { Address } from '@/application/shared/domain/Address';
import PrimaryContactReadOnly from '@/application/smallBusiness/infrastructure/components/PrimaryContactReadOnly.vue';
import CompanyInfoReadOnly from '@/application/smallBusiness/infrastructure/components/CompanyInfoReadOnly.vue';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';

interface emitInterface {
  (event: 'setLoading', value: boolean): void,
}

const emits = defineEmits<emitInterface>();

const { smallBusinessStore, smallBusinessState } = useSmallBusinessStore();
const { appStore, appState } = useAppStore();

const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);

const phoneRef = ref<{ handleSubmit: () => Promise<boolean> } | null>(null);
const mainAddressRef = ref<{ handleSubmit: () => Promise<boolean> } | null>(null);
const mailingAddressRef = ref<{ handleSubmit: () => Promise<boolean> } | null>(null);

const name = ref(smallBusinessState.value.application.company.name || '');
const first_name = ref(smallBusinessState.value.application.company.primary_contact?.first_name || '');
const last_name = ref(smallBusinessState.value.application.company.primary_contact?.last_name || '');
const main_address = ref(smallBusinessState.value.application.company.main_address || {} as Address);
const mailing_address = ref(smallBusinessState.value.application.company.mailing_address || {} as Address);
const sms_phone_consent = ref(smallBusinessState.value.application.company.sms_phone_consent || false);
const smsPhoneConsentTooltip = 'I consent to receive autodialed and pre-recorded calls and/or text/SMS messages at the telephone number I provided (including my cell phone number), from or on behalf of Covered California and/or Health For California Insurance Center. I understand that message and data rates may apply.';
const {
  value: sameMailingAddressAsMain,
  errorMessage: sameAsMainError,
} = useField('sameAsMain', 'yes_no_required', { initialValue: smallBusinessState.value.application.company.is_mailing_address_same_as_main });

const handleFormChange = (value: unknown, field: string): void => {
  smallBusinessStore.updateCompany({ [field]: value });
};

const sectionSubmit = async (): Promise<boolean> => {
  emits('setLoading', true);
  const valid = await runValidations();
  const phoneValid = await phoneRef.value?.handleSubmit();
  const mainAddressValid = await mainAddressRef.value?.handleSubmit();
  const mailingAddressValid = await mailingAddressRef.value?.handleSubmit() || sameMailingAddressAsMain.value;

  if (smallBusinessState.value.isReadOnly || (valid && phoneValid && mainAddressValid && mailingAddressValid && !appState.value.loadingCoverage)) {
    const response = await submitApplication();
    emits('setLoading', false);

    return response;
  }
  emits('setLoading', false);

  return false;
};

async function submitApplication (): Promise<boolean> {
  const result = await smallBusinessStore.submitForm();

  if (result && result.success) {
    return true;
  }

  if (result && result.error) {
    appStore.addToast({ message: result.error.message ?? 'Error sending the application. Please try again.', type: ToastTypes.DANGER });
  }

  return false;
}

defineExpose({
  sectionSubmit
});
</script>
<template>
  <BaseSection class="flex-1 flex">
    <div v-if="!smallBusinessState.isReadOnly">
      <div class="flex flex-col lg:flex-row gap-4">
        <div class="flex-1">
          <label class="font-medium whitespace-nowrap text-sky-700 block">
            First Name <span class="font-serif">*</span>
          </label>
          <Field v-model="first_name" type="text" v-slot="{ field, errorMessage, meta }" name="company-primary_contact_first_name" rules="required|min:3|max:100|not_one_of:Medicare">
            <input v-capitalized="field" v-bind="field" :class="{ ...meta }" @change="(v) => handleFormChange({ ...smallBusinessState.application.company.primary_contact, first_name}, 'primary_contact')">
            <span v-if="errorMessage" class="error">{{ errorMessage }}</span>
          </Field>
        </div>
        <div class="flex-1">
          <label class="font-medium whitespace-nowrap text-sky-700 block">
            Last Name <span class="font-serif">*</span>
          </label>
          <Field v-model="last_name" type="text" v-slot="{ field, errorMessage, meta }" name="company-primary_contact_last_name" rules="required|min:3|max:100|not_one_of:Medicare">
            <input v-capitalized="field" v-bind="field" :class="{ ...meta }" @change="(v) => handleFormChange({ ...smallBusinessState.application.company.primary_contact, last_name}, 'primary_contact')">
            <span v-if="errorMessage" class="error">{{ errorMessage }}</span>
          </Field>
        </div>
      </div>
      <div class="flex flex-col lg:flex-row gap-4">
        <div class="flex-1">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Phone <span class="font-serif">*</span>
          </label>
          <QuestionPhone :value="{ number: smallBusinessState.application.company.primary_contact?.phone }" phone-input-placeholder="" @update:value="(v: ContactPhone) => handleFormChange({ ...smallBusinessState.application.company.primary_contact, phone: v.number}, 'primary_contact')" ref="phoneRef" :hide-ext="true" :hide-type="true" />
        </div>
        <div class="flex-1">
          <label class="font-medium mb-1 whitespace-nowrap text-sky-700 block">
            Email Address <span class="font-serif">*</span>
          </label>
          <QuestionEmail :value="smallBusinessState.application.company.primary_contact?.email" :disable-placeholder="true" @update:value="(v: string) => handleFormChange({ ...smallBusinessState.application.company.primary_contact, email: v}, 'primary_contact')" id="company-email" :use-form-context="true" />
        </div>
      </div>
    </div>
    <PrimaryContactReadOnly v-else />
    <div v-if="!smallBusinessState.isReadOnly">
      <div class="flex flex-col lg:flex-row gap-4">
        <div class="flex-1">
          <label class="font-medium whitespace-nowrap text-sky-700 block">
            Legal Company Name <span class="font-serif">*</span>
          </label>
          <Field v-model="name" type="text" v-slot="{ field, errorMessage, meta }" name="company-name" rules="required|min:3|max:100|not_one_of:Medicare">
            <input v-capitalized="field" v-bind="field" :class="{ ...meta }" @change="(v) => handleFormChange(name, 'name')">
            <span class="error">{{ errorMessage }}</span>
          </Field>
        </div>
      </div>
      <div class="flex flex-col lg:flex-row gap-4">
        <div class="flex-1">
          <QuestionAddress :value="main_address" address-type="CA Headquarters" @update:value="v => handleFormChange({ ...smallBusinessState.application.company.main_address, ...v, type: ADDRESS_TYPE.HOME }, 'main_address')" ref="mainAddressRef" id="company-main_address" :application-type="APPLICATION_TYPES.SMALL_BUSINESS" :validate-coverage="true" :zip-read-only="true" />
        </div>
      </div>
      <div class="flex flex-col lg:flex-row gap-4">
        <div class="flex-1">
          <QuestionYesNo v-model="sameMailingAddressAsMain" question-text="Is mailing address the same as your Address (CA Headquarters)" id="sameAsMain" name="sameAsMain" @update:modelValue="(v?: boolean) => handleFormChange(v, 'is_mailing_address_same_as_main')" :disabled="smallBusinessState.isReadOnly"/>
          <span v-if="sameAsMainError" class="error">{{ sameAsMainError }}</span>
        </div>
      </div>
      <div v-if="sameMailingAddressAsMain === false" class="flex flex-col lg:flex-row gap-4 mt-4">
        <div class="flex-1">
          <QuestionAddress :auto-focus="true" :value="mailing_address" address-type="Mailing" @update:value="v => handleFormChange({ ...smallBusinessState.application.company.mailing_address, ...v, type: ADDRESS_TYPE.MAIL }, 'mailing_address')" ref="mailingAddressRef" id="company-mailing_address" :application-type="APPLICATION_TYPES.SMALL_BUSINESS" :validate-coverage="true" />
        </div>
      </div>
      <div class="flex flex-col lg:flex-row gap-4 mt-4">
        <div class="flex-1">
          <Field v-model="sms_phone_consent" :value="true" :unchecked-value="false" type="checkbox" v-slot="{ field, errorMessage }" name="company-sms_phone_consent" rules="required">
            <div class="flex flex-row items-start">
              <input type="checkbox" id="company-sms_phone_consent" class="border-gray-300 rounded-md w-4 h-4 mt-1 cursor-pointer" v-bind="field" @change="handleFormChange(sms_phone_consent, 'sms_phone_consent')">
              <label for="company-sms_phone_consent" class="font-medium text-md ml-2 cursor-pointer">
                <small>
                  I agree to receive text messages and calls about my health care coverage from Covered California and Health For California Insurance Center. <span class="font-serif">*</span>
                  <hfc-icon icon="question-circle" class="ml-1 inline-block w-3 h-3 text-blue-500" tabindex="-1" v-tooltip="{ content: smsPhoneConsentTooltip, popperClass: 'w-64' }" />
                </small>
              </label>
            </div>
            <span v-if="errorMessage" class="error">{{ errorMessage }}</span>
          </Field>
        </div>
      </div>
    </div>
    <CompanyInfoReadOnly v-else />
  </BaseSection>
</template>
